import type { ReactNode } from "react";
import type { DashboardStats } from "../lib/commandCenter";
import { IconPlus } from "../lib/icons";
import { creditLevel, formatCredits } from "../lib/credits";

/**
 * The dashboard header at the top of the Calendar page: a time-of-day greeting,
 * the workspace title (a WorkspaceSwitcher enlarged so its name + chevron read
 * as the page title), a real stat row derived from live dispatch/scheduled data,
 * and the single strong red "Create post" call to action.
 */
export function DashboardHeader({
  greeting,
  firstName,
  stats,
  credits,
  onManageCredits,
  onViewFailed,
  workspaceSwitcher,
  onCreate,
}: {
  greeting: string;
  firstName: string;
  stats: DashboardStats;
  /** Live credit balance, or null when billing isn't configured (then hidden). */
  credits?: number | null;
  /** Open the billing surface — the credits stat is a button when provided. */
  onManageCredits?: () => void;
  /** Open the Activity log's failures — the "failed" stat is a button when there
   * are failures and this is provided, so an alarming number isn't a dead-end. */
  onViewFailed?: () => void;
  workspaceSwitcher: ReactNode;
  onCreate: () => void;
}) {
  return (
    <div className="cc-dash-head">
      <div>
        <p className="cc-greeting">
          {greeting}, {firstName}
        </p>
        <div className="cc-title-switcher">{workspaceSwitcher}</div>

        <div className="cc-stats">
          <span className="cc-stat">
            <span className="cc-stat-n">{stats.scheduled}</span>
            <span className="cc-stat-label">scheduled</span>
          </span>
          <span className="cc-stat-sep" aria-hidden="true" />
          <span className="cc-stat">
            <span className="cc-stat-n">{stats.publishedThisWeek}</span>
            <span className="cc-stat-label">published this week</span>
          </span>
          <span className="cc-stat-sep" aria-hidden="true" />
          {stats.failed > 0 && onViewFailed ? (
            <button
              type="button"
              className="cc-stat cc-stat-failed"
              onClick={onViewFailed}
              title="See what failed and how to fix it"
            >
              <span className="cc-stat-n is-fail">{stats.failed}</span>
              <span className="cc-stat-label">failed →</span>
            </button>
          ) : (
            <span className="cc-stat">
              <span className={`cc-stat-n${stats.failed > 0 ? " is-fail" : ""}`}>
                {stats.failed}
              </span>
              <span className="cc-stat-label">failed</span>
            </span>
          )}
          {typeof credits === "number" &&
            (() => {
              const level = creditLevel(credits);
              const levelClass =
                level === "critical" ? " cc-stat-n--critical" : level === "low" ? " cc-stat-n--low" : "";
              return (
                <>
                  <span className="cc-stat-sep" aria-hidden="true" />
                  <button
                    type="button"
                    className="cc-stat cc-stat-credits"
                    onClick={onManageCredits}
                    title={
                      level === "ok"
                        ? "Manage credits & billing"
                        : "Running low — top up or upgrade to keep publishing"
                    }
                  >
                    <span className={`cc-stat-n${levelClass}`}>{formatCredits(credits)}</span>
                    <span className="cc-stat-label">credits{level !== "ok" ? " · low" : ""}</span>
                  </button>
                </>
              );
            })()}
        </div>
      </div>

      <button type="button" className="btn primary cc-create" onClick={onCreate}>
        <IconPlus size={17} />
        Create post
      </button>
    </div>
  );
}
